import { create } from "zustand";
import type { AxiosResult } from "@/lib/axios";
import type { MembershipRequest, MembershipState, MembershipResponse, UserRole } from "../types";
import { membershipApi } from "../services/membership-api";

export const useMembershipStore = create<MembershipState>((set) => ({
	memberships: null,
	invites: null,

	indexMembership: async (workspaceId: string): Promise<AxiosResult<MembershipResponse[] | null>> => {
		const result = await membershipApi.index(workspaceId);

		if (result.success && result.data) {
			const invites = result.data?.filter((membership) => membership.accepted_at === null) || [];
			const memberships = result.data?.filter((membership) => membership.accepted_at !== null) || [];
			set({ memberships: memberships, invites: invites });
		}

		return result;
	},

	createMembership: async (workspaceId: string, payload: MembershipRequest): Promise<AxiosResult<MembershipResponse | null>> => {
		const result = await membershipApi.create(workspaceId, payload);
		if (result.success && result.data) {
			set((state) => ({
				invites: state.invites ? [...state.invites, result.data as MembershipResponse] : [result.data as MembershipResponse],
			}));
		}

		return result;
	},

	updateMembershipRole: async (id: string, workspaceId: string, role: UserRole): Promise<AxiosResult<MembershipResponse | null>> => {
		set((state) => ({
			memberships: state.memberships?.map((membership) => {
				if (membership.id === id) {
					const user = membership.user;
					return { ...membership, user: user, role: role };
				}
				return membership;
			}),
			invites: state.invites?.map((invite) => {
				if (invite.id === id) {
					return { ...invite, role: role };
				}
				return invite;
			}),
		}));

		const result = await membershipApi.updateRole(id, workspaceId, role);

		if (result.success && result.data) {
			set((state) => ({
				memberships: state.memberships?.map((membership) => {
					if (membership.id === id) {
						// Preserve user object if not included in API response
						return {
							...(result.data as MembershipResponse),
							user: (result.data as MembershipResponse).user || membership.user,
						};
					}
					return membership;
				}),
			}));
		}

		return result;
	},

	deleteMembership: async (id: string, workspaceId: string): Promise<AxiosResult<void>> => {
		const result = await membershipApi.delete(id, workspaceId);

		if (result.success) {
			set((state) => ({
				memberships: state.memberships ? state.memberships.filter((membership) => membership.id !== id) : null,
				invites: state.invites ? state.invites.filter((invite) => invite.id !== id) : null,
			}));
		}

		return result;
	},

	resendMembershipInvitation: async (id: string, workspaceId: string): Promise<AxiosResult<MembershipResponse | null>> => {
		return await membershipApi.resendInvitation(id, workspaceId);
	},
}));
